// Canva Connect – Konto verbinden, Designs durchsuchen, als Bild/PDF in die Mediathek übernehmen
import React, { useState, useEffect } from "react";
import axios from "axios";
import { Palette, Loader2, Unplug, Search, Download, Copy } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/canva`;

export function CanvaPanel({ flash, onImported, compact = false }) {
  const [st, setSt] = useState(null);
  const [designs, setDesigns] = useState([]);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState("");
  const load = async () => setSt((await axios.get(`${API}/status`)).data);
  const loadDesigns = async (query) => {
    setBusy("list");
    try { setDesigns((await axios.get(`${API}/designs`, { params: query ? { query } : {} })).data.items); }
    catch (e) { flash(e.response?.data?.detail || "Canva-Fehler"); }
    finally { setBusy(""); }
  };
  useEffect(() => {
    load().then(() => {
      const r = new URLSearchParams(window.location.search).get("canva");
      if (r) {
        flash({ connected: "✓ Canva verbunden", denied: "Canva-Verbindung abgelehnt", invalid_state: "Verbindung abgelaufen – bitte erneut starten", error: "Canva-Verbindung fehlgeschlagen" }[r] || r);
        window.history.replaceState({}, "", window.location.pathname);
      }
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (st?.connected) loadDesigns(""); }, [st?.connected]); // eslint-disable-line react-hooks/exhaustive-deps
  const connect = async () => {
    setBusy("connect");
    try { const r = await axios.get(`${API}/connect`); window.location.assign(r.data.url); }
    catch (e) { flash(e.response?.data?.detail || "Canva-Verbindung konnte nicht gestartet werden"); setBusy(""); }
  };
  const importDesign = async (d, format) => {
    setBusy(d.id + format);
    try {
      const r = await axios.post(`${API}/import`, { design_id: d.id, format, title: d.title });
      flash(`✓ „${d.title}“ als ${format.toUpperCase()} in die Mediathek übernommen`);
      onImported && onImported(r.data);
    } catch (e) { flash(e.response?.data?.detail || "Import fehlgeschlagen"); }
    finally { setBusy(""); }
  };
  if (!st) return null;
  return (
    <div className="card space-y-2 border-[#7D2AE8]/30" data-testid="canva-panel">
      <div className="flex items-center justify-between gap-2">
        <div className="overline flex items-center gap-1"><Palette size={12} className="text-[#7D2AE8]" /> Canva · Plakate & Designs</div>
        <span className="channel-pill" data-testid="canva-status" style={{ background: st.connected ? "#10B98122" : "#9CA3AF22", color: st.connected ? "#10B981" : "#9CA3AF" }}>
          {st.connected ? `verbunden${st.user ? ` · ${st.user}` : ""}` : st.configured ? "nicht verbunden" : "App fehlt"}
        </span>
      </div>
      {!st.connected && (
        <>
          <button className="btn btn-primary w-full justify-center" onClick={connect} disabled={!st.configured || !!busy} data-testid="canva-connect-btn">
            {busy === "connect" ? <Loader2 size={14} className="animate-spin" /> : <Palette size={14} />} Mit Canva verbinden
          </button>
          <div className="text-[10px] text-eb-muted space-y-1">
            {!st.configured && <div className="text-amber-300">Canva-Integration noch nicht hinterlegt: Unter canva.com/developers eine Integration anlegen, Client-ID + Secret an Sunworker geben.</div>}
            <div className="flex items-center gap-1">Redirect-URL für Canva:
              <code className="font-mono bg-black/30 px-1 rounded truncate" data-testid="canva-redirect-uri">{st.redirect_uri}</code>
              <button className="btn !py-0 !px-1" onClick={() => { navigator.clipboard.writeText(st.redirect_uri); flash("✓ Redirect-URL kopiert"); }} data-testid="canva-copy-redirect"><Copy size={10} /></button>
            </div>
          </div>
        </>
      )}
      {st.connected && (
        <>
          <div className="flex gap-1">
            <input placeholder="Designs suchen (z. B. Herbstfest)" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && loadDesigns(q)} data-testid="canva-search" />
            <button className="btn" onClick={() => loadDesigns(q)} disabled={busy === "list"} data-testid="canva-search-btn">{busy === "list" ? <Loader2 size={13} className="animate-spin" /> : <Search size={13} />}</button>
            <button className="btn text-rose-400" onClick={() => window.confirm("Canva trennen?") && axios.post(`${API}/disconnect`).then(() => { flash("Canva getrennt"); load(); })} data-testid="canva-disconnect"><Unplug size={13} /></button>
          </div>
          <div className={`grid ${compact ? "grid-cols-2" : "grid-cols-2 md:grid-cols-4"} gap-2 max-h-[46vh] overflow-y-auto`} data-testid="canva-designs">
            {designs.length === 0 && busy !== "list" && <div className="text-xs text-eb-muted col-span-full">Keine Designs gefunden.</div>}
            {designs.map((d) => (
              <div key={d.id} className="eb-surface rounded-md overflow-hidden text-[11px]" data-testid={`canva-design-${d.id}`}>
                <div className="aspect-[4/5] bg-black/30">{d.thumbnail && <img src={d.thumbnail} alt="" className="w-full h-full object-cover" />}</div>
                <div className="p-1.5 space-y-1">
                  <div className="font-medium truncate" title={d.title}>{d.title}</div>
                  <div className="flex gap-1">
                    {["png", "pdf"].map((f) => (
                      <button key={f} className="btn !py-0.5 !px-1.5 text-[10px] flex-1 justify-center" onClick={() => importDesign(d, f)} disabled={!!busy} data-testid={`canva-import-${f}-${d.id}`}>
                        {busy === d.id + f ? <Loader2 size={10} className="animate-spin" /> : <Download size={10} />} {f.toUpperCase()}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
